import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import Loader from '../../../components/common/Loader';
import { getOrgUsers, deleteUser, toggleUserStatus, getRolesAndPermissions, createAdminUser, editOrganisationUser, getTraineeGroups } from '../../../services/adminService';
import { toast } from 'react-toastify';
import countryData from '../../../utils/countory.json';
import { usePermissions } from '../../../context/PermissionsContext';

// Password input field component for reuse (moved outside to prevent re-creation)
const PasswordInput = ({ id, label, value, onChange, isRequired, placeholder, error, showPassword, setShowPassword }) => (
  <div className="mb-3">
    <label htmlFor={id} className="block mb-1">{label}</label>
    <div className="relative">
      <input
        type={showPassword ? "text" : "password"}
        className={`w-full px-3 py-2 border ${error ? 'border-red-500' : 'border-input-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 ${error ? 'focus:ring-red-500/20' : ''}`}
        id={id}
        name="userPassword"
        value={value}
        onChange={onChange}
        required={isRequired}
        placeholder={placeholder}
      />
      <div
        className="absolute right-0 top-1/2 -translate-y-1/2 px-2 cursor-pointer z-[5]"
        onClick={() => setShowPassword(!showPassword)}
      >
        <Icon
          icon={showPassword ? "mdi:eye-off" : "mdi:eye"}
          width="20"
          height="20"
          className="text-gray-500"
        />
      </div>
      {error && (
        <div className="text-red-500 text-sm mt-1">
          {error}
        </div>
      )}
    </div>
  </div>
);

function Users() {
  const { permissions } = usePermissions();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [togglingStatusId, setTogglingStatusId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [actionType, setActionType] = useState('Create'); // Add this line
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [traineeGroups, setTraineeGroups] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+65', // Default to Singapore
    password: '',
    role: '',
    profilePhoto: null,
    profilePhotoPreview: null,
    traineeGroupIds: []
  });

  const [formErrors, setFormErrors] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: '',
    profilePhoto: ''
  });

  const [searchCountry, setSearchCountry] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Filter countries based on search
  const filteredCountries = countryData.countoryCode.filter(country =>
    country.country.toLowerCase().includes(searchCountry.toLowerCase()) ||
    country.code.includes(searchCountry)
  );

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getOrgUsers({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        state: statusFilter
      });

      // console.log('users response--------------', response);

      if (response.success) {
        setUsers(response.data.users);
        setTotalPages(response.data.pagination.totalPages);
        setTotalUsers(response.data.pagination.totalCount);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [currentPage, itemsPerPage, searchQuery, statusFilter]);

  // Add new function to fetch roles
  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await getRolesAndPermissions({
        page: 1,
        limit: 100, // Get all roles
        state: 'active',
        search: ''
      });

      // console.log("roles response--------------", response.data.rolesWithPermissions);

      if (response.success) {
        // Filter only active roles
        const activeRoles = response.data.rolesWithPermissions.filter(role => role.is_active === 1);
        setRoles(activeRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  useEffect(() => {
    fetchRoles(); // Fetch roles when component mounts
  }, []);

  // Handle opening the create modal
  const handleOpenCreateModal = () => {
    if (!permissions.roles_and_access_user_create) {
      toast.warning("You don't have permission to create users");
      return;
    }
    // Clear search input to prevent autocomplete issues
    setSearchQuery('');

    // Reset form data first
    setFormData({
      name: '',
      email: '',
      phone: '',
      countryCode: '+91',
      password: '',
      role: '',
      profilePhoto: null,
      profilePhotoPreview: null,
      traineeGroupIds: []
    });
    setFormErrors({});
    setActionType('Create'); // Add this line
    fetchTraineeGroupsForModal();
    setShowCreateModal(true);
  };

  // Handle closing the create modal
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    handleRemovePhoto();
    setFormData({
      name: '',
      email: '',
      phone: '',
      countryCode: '+91',
      password: '',
      role: '',
      profilePhoto: null,
      profilePhotoPreview: null,
      traineeGroupIds: []
    });
    setFormErrors({});
  };

  const fetchTraineeGroupsForModal = async () => {
    try {
      const res = await getTraineeGroups();
      if (res?.success && Array.isArray(res?.data?.groups)) setTraineeGroups(res.data.groups);
      else if (res?.success && Array.isArray(res?.data)) setTraineeGroups(res.data);
      else setTraineeGroups([]);
    } catch {
      setTraineeGroups([]);
    }
  };

  // Handle opening the edit modal
  const handleOpenEditModal = async (user) => {
    if (!permissions.roles_and_access_user_edit) {
      toast.warning("You don't have permission to edit users");
      return;
    }
    // Clear search input to prevent autocomplete issues
    setSearchQuery('');

    // Fetch latest roles before opening modal
    setLoadingRoles(true);
    try {
      const response = await getRolesAndPermissions({
        page: 1,
        limit: 100,
        state: 'active',
        search: ''
      });

      if (response.success) {
        const activeRoles = response.data.rolesWithPermissions.filter(role => role.is_active === 1);
        setRoles(activeRoles);
      }
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to fetch roles');
      return; // Don't open modal if roles fetch fails
    } finally {
      setLoadingRoles(false);
    }

    setSelectedUser(user);
    setActionType('Edit');
    // Find the role ID based on role name
    const userRole = roles.find(role => role.role_name.toLowerCase() === user.role_name.toLowerCase());

    if (!userRole) {
      console.warn(`Role not found for role_name: ${user.role_name}`);
    }

    // Populate form data with user information
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.mobile || '',
      countryCode: user.mobileno_code || '+91',
      password: '', // Don't populate password for security
      role: userRole?.role_id || '',
      profilePhoto: null,
      profilePhotoPreview: user.profile_pic_url || null,
      traineeGroupIds: Array.isArray(user.trainee_group_ids) ? user.trainee_group_ids : []
    });
    setFormErrors({});
    fetchTraineeGroupsForModal();
    setShowEditModal(true);
  };

  // Handle closing the edit modal
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedUser(null);
    setActionType('Create'); // Add this line
    handleRemovePhoto();
    setFormData({
      name: '',
      email: '',
      phone: '',
      countryCode: '+91',
      password: '',
      role: '',
      profilePhoto: null,
      profilePhotoPreview: null,
      traineeGroupIds: []
    });
    setFormErrors({});
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleLimitChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Map form field names to state properties
    const fieldMap = {
      'userName': 'name',
      'userEmail': 'email',
      'userPhone': 'phone',
      'userPassword': 'password',
      'countryCode': 'countryCode',
      'role': 'role'
    };

    const stateField = fieldMap[name] || name;

    setFormData(prev => ({
      ...prev,
      [stateField]: value
    }));

    // Clear error when user starts typing
    setFormErrors(prev => ({
      ...prev,
      [stateField]: ''
    }));
  };

  const handleProfilePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB
        setFormErrors(prev => ({
          ...prev,
          profilePhoto: 'File size should not exceed 5MB'
        }));
        return;
      }

      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        setFormErrors(prev => ({
          ...prev,
          profilePhoto: 'Please upload a valid image file (JPG, JPEG, PNG)'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        profilePhoto: file,
        profilePhotoPreview: URL.createObjectURL(file)
      }));
      setFormErrors(prev => ({
        ...prev,
        profilePhoto: ''
      }));
    }
  };

  const handleRemovePhoto = () => {
    if (formData.profilePhotoPreview) {
      URL.revokeObjectURL(formData.profilePhotoPreview);
    }
    setFormData(prev => ({
      ...prev,
      profilePhoto: null,
      profilePhotoPreview: null
    }));
  };

  // Password validation helper function
  const validatePassword = (password) => {
    const errors = [];

    // Check minimum length
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }

    // Check for letters
    if (!/[a-zA-Z]/.test(password)) {
      errors.push('Password must contain at least one letter');
    }

    // Check for numbers
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    // Check for special characters
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    return errors;
  };

  const handleCreateUser = async () => {
    // Validate form
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (formData.phone && !/^\d+$/.test(formData.phone.trim())) {
      errors.phone = 'Phone number should contain only digits';
    }
    if (!formData.role) {
      errors.role = 'Role is required';
    }

    // Enhanced password validation for Create
    if (!formData.password) {
      errors.password = 'Password is required';
    } else {
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        errors.password = passwordErrors.join('. ');
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('mobile', formData.phone.trim());
      formDataToSend.append('mobileno_code', formData.countryCode);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('role_id', formData.role);
      formDataToSend.append('notify_by_email', 'true');
      if (Array.isArray(formData.traineeGroupIds) && formData.traineeGroupIds.length > 0) {
        formDataToSend.append('trainee_group_ids', JSON.stringify(formData.traineeGroupIds));
      }

      // Handle profile picture
      if (formData.profilePhoto) {
        formDataToSend.append('profile_pic', formData.profilePhoto);
      } else {
        formDataToSend.append('profile_pic', null);
      }

      const response = await createAdminUser(formDataToSend);

      if (response.success) {
        toast.success(response.message || 'User created successfully');
        // Refresh the users list
        fetchUsers();
        // Close modal and reset form
        setShowCreateModal(false);
        setFormData({
          name: '',
          email: '',
          phone: '',
          countryCode: '+91',
          password: '',
          role: '',
          profilePhoto: null,
          profilePhotoPreview: null,
          traineeGroupIds: []
        });
        setFormErrors({});
      } else {
        toast.error(response.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.message || 'Error creating user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteClick = (user) => {
    if (!permissions.roles_and_access_user_delete) {
      toast.warning("You don't have permission to delete users");
      return;
    }
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    setIsDeleting(true);
    try {
      const response = await deleteUser(selectedUser.id);
      if (response.success) {
        // Refresh the users list
        fetchUsers();
        // Show success message if needed
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      // Handle error if needed
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  };

  // Add this new function to handle direct status toggle
  const handleStatusToggle = async (user) => {
    if (!permissions.roles_and_access_user_status) {
      toast.warning("You don't have permission to change user status");
      return;
    }
    setTogglingStatusId(user.id);
    try {
      const newStatus = user.is_blocked === 0 ? 1 : 0;
      const response = await toggleUserStatus(user.id, newStatus);
      if (response.success) {
        // Update user status locally
        setUsers(prevUsers => prevUsers.map(u =>
          u.id === user.id ? { ...u, is_blocked: newStatus } : u
        ));
        // Show success notification
        toast.success(`User ${newStatus === 0 ? 'unblocked' : 'blocked'} successfully`);
      }
    } catch (error) {
      console.error('Error toggling user status:', error);
      toast.error('Failed to update user status');
    } finally {
      setTogglingStatusId(null);
    }
  };

  // Handle edit user submit
  const handleEditUser = async () => {
    // Validate form (similar to create but password is optional)
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    if (formData.phone && !/^\d+$/.test(formData.phone.trim())) {
      errors.phone = 'Phone number should contain only digits';
    }
    if (!formData.role) {
      errors.role = 'Role is required';
    }

    // Password validation (only if password is provided in edit mode)
    if (formData.password && formData.password.trim() !== '') {
      const passwordErrors = validatePassword(formData.password);
      if (passwordErrors.length > 0) {
        errors.password = passwordErrors.join('. ');
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsEditing(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('mobile', formData.phone.trim());
      formDataToSend.append('role_id', formData.role);
      formDataToSend.append('mobileno_code', formData.countryCode || '+65');

      // loop through formDataToSend and log the key and value
      for (const [key, value] of formDataToSend.entries()) {
        // console.log("key", key);
        // console.log(`${key}: ${value}`);
      }

      // Only append password if it's provided and passes validation
      if (formData.password && formData.password.trim() !== '') {
        formDataToSend.append('password', formData.password.trim());
      }

      // Handle profile picture
      if (formData.profilePhoto) {
        formDataToSend.append('profile_pic', formData.profilePhoto);
      }
      if (Array.isArray(formData.traineeGroupIds)) {
        formDataToSend.append('trainee_group_ids', JSON.stringify(formData.traineeGroupIds));
      }

      const response = await editOrganisationUser(formDataToSend, selectedUser.id);

      if (response.success) {
        toast.success(response.message || 'User updated successfully');
        // Refresh the users list
        fetchUsers();
        // Close modal and reset form
        handleCloseEditModal();
      } else {
        console.error("Failed to edit user:", response.message);
        toast.error(`Failed to edit user: ${response.message}`);
      }

    } catch (error) {
      console.error('Error editing user:', error);
      toast.error(error.response?.data?.message || 'Error updating user');
    } finally {
      setIsEditing(false);
    }
  };

  const getFormattedMobileNumber = (mobile, mobileCode) => {
    if (!mobile) return '--';
    return mobileCode ? `${mobileCode} ${mobile}` : mobile;
  };

  // Add click outside handler to close country dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown && !event.target.closest('.position-relative')) {
        setShowCountryDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showCountryDropdown]);

  // Clear search when modals are closed to prevent autocomplete issues
  useEffect(() => {
    if (!showCreateModal && !showEditModal) {
      // Small delay to ensure modal is fully closed
      const timer = setTimeout(() => {
        const searchInput = document.getElementById('userSearchInput');
        if (searchInput && searchInput.value !== searchQuery) {
          searchInput.value = searchQuery;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showCreateModal, showEditModal, searchQuery]);


  return (
    <div className="space-y-6">
      {/* Search, Filter and Actions Toolbar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div className="flex flex-1 flex-col sm:flex-row items-center gap-4 w-full">
          <div className="relative flex-1 w-full group/search">
            <Icon icon="fluent:search-20-regular" className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 group-focus-within/search:text-primary transition-colors" />
            <input
              id="userSearchInput"
              type="search"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search team members..."
              className="w-full pl-11 pr-4 py-3 rounded-2xl border border-gray-100 bg-white text-sm shadow-sm focus:outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/30 transition-all font-medium"
              autoComplete="off"
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">


            <div className="flex-1 sm:flex-none flex items-center gap-2 bg-white p-1 rounded-2xl shadow-sm border border-gray-100 min-w-[140px]">
                <Icon icon="fluent:filter-20-regular" className="ml-3 w-4 h-4 text-gray-400" />
                <select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  className="w-full pl-2 pr-8 py-2.5 rounded-xl bg-transparent text-sm font-semibold focus:outline-none cursor-pointer"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                </select>
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={handleOpenCreateModal}
          disabled={!permissions.roles_and_access_user_create}
          className="inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-2xl bg-primary text-white text-sm font-bold shadow-xl shadow-primary/20 hover:bg-primary/90 transition-all hover:scale-[1.02] active:scale-[0.98] w-full lg:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Icon icon="fluent:person-add-24-regular" width="20" height="20" />
          Add Team Member
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-gray-100 border-dashed">
            <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mb-4" />
            <p className="text-gray-500 font-medium">Loading team members...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="py-20 text-center bg-white rounded-3xl border border-gray-100 border-dashed shadow-sm">
          <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon icon="fluent:people-search-24-regular" className="text-gray-300 w-10 h-10" />
          </div>
          <h3 className="text-gray-900 font-bold text-lg">No Team Members Found</h3>
          <p className="text-gray-500 max-w-xs mx-auto mt-2">Try adjusting your search or filters to find what you're looking for.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-[24px] border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Member</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Role</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-4 text-[11px] font-bold text-gray-400 uppercase tracking-wider text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/70 transition-colors group">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className="relative">
                              {user.profile_pic_url ? (
                                  <img src={user.profile_pic_url} className="w-11 h-11 rounded-2xl object-cover ring-2 ring-white shadow-sm" alt={user.name} />
                              ) : (
                                  <div className="w-11 h-11 rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 border border-gray-200">
                                      <Icon icon="fluent:person-24-regular" width="22" />
                                  </div>
                              )}
                              <div className={`absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white ${user.is_blocked === 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-bold text-gray-900 truncate">{user.name}</p>
                            <p className="text-xs text-gray-500 truncate">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <span className="inline-flex items-center px-3 py-1 rounded-lg text-xs font-bold bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/50">
                          {user.role_name}
                        </span>
                      </td>
                      <td className="px-6 py-5">
                        <p className="text-sm font-semibold text-gray-700">{getFormattedMobileNumber(user.mobile, user.mobileno_code)}</p>
                      </td>
                      <td className="px-6 py-5">
                        <button
                          onClick={() => handleStatusToggle(user)}
                          disabled={togglingStatusId === user.id || !permissions.roles_and_access_user_status}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                              user.is_blocked === 0
                              ? 'text-green-600 bg-green-50 hover:bg-green-100 ring-1 ring-green-200/50'
                              : 'text-red-600 bg-red-50 hover:bg-red-100 ring-1 ring-red-200/50'
                          }`}
                        >
                          {togglingStatusId === user.id ? (
                              <Icon icon="fluent:spinner-20-regular" className="animate-spin" />
                          ) : (
                              <Icon icon={user.is_blocked === 0 ? "fluent:checkmark-circle-24-regular" : "fluent:error-circle-24-regular"} />
                          )}
                          {user.is_blocked === 0 ? 'Active' : 'Blocked'}
                        </button>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => handleOpenEditModal(user)}
                            disabled={!permissions.roles_and_access_user_edit}
                            className="p-2.5 rounded-xl text-gray-500 hover:text-primary hover:bg-primary/5 transition-all"
                          >
                            <Icon icon="fluent:edit-24-regular" width="20" />
                          </button>
                          <button
                            onClick={() => handleDeleteClick(user)}
                            disabled={!permissions.roles_and_access_user_delete}
                            className="p-2.5 rounded-xl text-gray-500 hover:text-red-600 hover:bg-red-50 transition-all"
                          >
                            <Icon icon="fluent:delete-24-regular" width="20" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination Hub */}
          {totalUsers > 0 && (
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-gray-50/50 rounded-[24px] border border-gray-100">
              <p className="text-sm font-medium text-gray-500">
                Showing <span className="text-gray-900 font-bold">{users.length}</span> of <span className="text-gray-900 font-bold">{totalUsers}</span> team members
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <Icon icon="fluent:chevron-left-24-regular" width="20" />
                  </button>
                  <div className="flex items-center px-4 py-2 bg-white rounded-xl border border-gray-100 shadow-sm">
                    <span className="text-sm font-bold text-primary">{currentPage}</span>
                    <span className="text-sm font-medium text-gray-300 mx-1">/</span>
                    <span className="text-sm font-medium text-gray-500">{totalPages}</span>
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-xl bg-white border border-gray-100 text-gray-500 hover:text-primary disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-all"
                  >
                    <Icon icon="fluent:chevron-right-24-regular" width="20" />
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}



        {/* Create User Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[88vh]">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-2xl flex-shrink-0">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                    <Icon icon="fluent:person-add-24-regular" width={18} height={18} className="text-white" />
                  </div>
                  <h5 className="text-base font-semibold text-gray-900">Add New User</h5>
                </div>
                <button type="button" className="w-8 h-8 rounded-lg hover:bg-white/60 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors" onClick={handleCloseCreateModal}>
                  <Icon icon="fluent:dismiss-24-regular" width={18} height={18} />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto flex-1 p-6">
                {/* Avatar + Name + Email */}
                <div className="flex items-start gap-5 mb-5">
                  <div className="flex-shrink-0 flex flex-col items-center">
                    <div className="relative cursor-pointer group" onClick={() => document.getElementById('profilePhotoUpload').click()}>
                      {formData.profilePhotoPreview ? (
                        <>
                          <img src={formData.profilePhotoPreview} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200 shadow-sm" />
                          <button className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow" onClick={e => { e.stopPropagation(); handleRemovePhoto(); }}>
                            <Icon icon="fluent:dismiss-12-regular" width={10} height={10} />
                          </button>
                        </>
                      ) : (
                        <div className="w-16 h-16 rounded-full bg-indigo-50 border-2 border-dashed border-indigo-300 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                          <Icon icon="fluent:camera-add-24-regular" width={22} height={22} className="text-indigo-400" />
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Photo</p>
                    <input type="file" id="profilePhotoUpload" className="hidden" accept="image/jpeg,image/png,image/jpg" onChange={handleProfilePhotoUpload} />
                    {formErrors.profilePhoto && <div className="text-red-500 text-xs mt-1 text-center">{formErrors.profilePhoto}</div>}
                  </div>
                  <div className="flex-1 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <input type="text" className={`w-full px-3 py-2 text-sm border ${formErrors.name ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                        name="userName" value={formData.name} onChange={handleInputChange} placeholder="Full name" autoComplete="off" />
                      {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
                      <input type="email" className={`w-full px-3 py-2 text-sm border ${formErrors.email ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                        name="userEmail" value={formData.email} onChange={handleInputChange} placeholder="email@example.com" autoComplete="off" />
                      {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
                    </div>
                  </div>
                </div>

                {/* Phone + Role */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="flex gap-2">
                      <select className="px-2 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 w-28 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                        name="countryCode" value={formData.countryCode} onChange={handleInputChange}>
                        {countryData.countoryCode.map((country, index) => (
                          <option key={index} value={country.code}>{country.country} ({country.code})</option>
                        ))}
                      </select>
                      <input type="tel" className={`flex-1 min-w-0 px-3 py-2 text-sm border ${formErrors.phone ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                        name="userPhone" value={formData.phone} onChange={handleInputChange} placeholder="Phone number" autoComplete="off" />
                    </div>
                    {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                    <select className={`w-full px-3 py-2 text-sm border ${formErrors.role ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                      name="role" value={formData.role} onChange={handleInputChange} disabled={loadingRoles}>
                      <option value="">Choose role...</option>
                      {loadingRoles ? <option disabled>Loading...</option> : roles.map(role => (
                        <option key={role.role_id} value={role.role_id}>{role.role_name}</option>
                      ))}
                    </select>
                    {formErrors.role && <p className="text-red-500 text-xs mt-1">{formErrors.role}</p>}
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'}
                      className={`w-full px-3 py-2 text-sm border ${formErrors.password ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 pr-10`}
                      name="userPassword" value={formData.password} onChange={handleInputChange} placeholder="Enter password" autoComplete="new-password" />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)}>
                      <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} width={18} height={18} />
                    </button>
                  </div>
                  {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {[
                      { label: '8+ chars', test: /^.{8,}$/ },
                      { label: 'Letter', test: /[a-zA-Z]/ },
                      { label: 'Number', test: /\d/ },
                      { label: 'Special char', test: /[!@#$%^&*(),.?":{}|<>]/ },
                    ].map(({ label, test }) => (
                      <span key={label} className={`flex items-center gap-1 text-xs ${test.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                        <Icon icon={test.test(formData.password) ? 'mdi:check-circle' : 'mdi:circle-outline'} width={12} height={12} />
                        {label}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl flex-shrink-0">
                <button type="button" className="px-5 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors" onClick={handleCloseCreateModal} disabled={isSubmitting}>
                  Cancel
                </button>
                <button type="button" className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium shadow-sm transition-colors inline-flex items-center gap-2" onClick={handleCreateUser} disabled={isSubmitting}>
                  {isSubmitting ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />Creating...</> : <><Icon icon="fluent:person-add-24-regular" width={16} height={16} />Create User</>}
                </button>
              </div>
            </div>
          </div>
        )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[88vh]">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-indigo-50 to-blue-50 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                  <Icon icon="fluent:person-edit-24-regular" width={18} height={18} className="text-white" />
                </div>
                <div>
                  <h5 className="text-base font-semibold text-gray-900">Edit User</h5>
                  <p className="text-xs text-gray-500 leading-tight">{selectedUser.name}</p>
                </div>
              </div>
              <button type="button" className="w-8 h-8 rounded-lg hover:bg-white/60 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors" onClick={handleCloseEditModal}>
                <Icon icon="fluent:dismiss-24-regular" width={18} height={18} />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto flex-1 p-6">
              {/* Avatar + Name + Email */}
              <div className="flex items-start gap-5 mb-5">
                <div className="flex-shrink-0 flex flex-col items-center">
                  <div className="relative cursor-pointer group" onClick={() => document.getElementById('editProfilePhotoUpload').click()}>
                    {formData.profilePhotoPreview ? (
                      <>
                        <img src={formData.profilePhotoPreview} alt="Profile" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200 shadow-sm" />
                        <button className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow" onClick={e => { e.stopPropagation(); handleRemovePhoto(); }}>
                          <Icon icon="fluent:dismiss-12-regular" width={10} height={10} />
                        </button>
                      </>
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-indigo-50 border-2 border-dashed border-indigo-300 flex items-center justify-center group-hover:bg-indigo-100 transition-colors">
                        <Icon icon="fluent:camera-add-24-regular" width={22} height={22} className="text-indigo-400" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Photo</p>
                  <input type="file" id="editProfilePhotoUpload" className="hidden" accept="image/jpeg,image/png,image/jpg" onChange={handleProfilePhotoUpload} />
                </div>
                <div className="flex-1 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                    <input type="text" className={`w-full px-3 py-2 text-sm border ${formErrors.name ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                      name="userName" value={formData.name} onChange={handleInputChange} placeholder="Full name" autoComplete="off" />
                    {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                    <input type="email" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                      name="userEmail" value={formData.email} readOnly />
                    <p className="text-xs text-gray-400 mt-1">Email cannot be edited</p>
                  </div>
                </div>
              </div>

              {/* Phone + Role */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                  <div className="flex gap-2">
                    <select className="px-2 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 w-28 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      name="countryCode" value={formData.countryCode} onChange={handleInputChange}>
                      {countryData.countoryCode.map((country, index) => (
                        <option key={index} value={country.code}>{country.country} ({country.code})</option>
                      ))}
                    </select>
                    <input type="tel" className={`flex-1 min-w-0 px-3 py-2 text-sm border ${formErrors.phone ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                      name="userPhone" value={formData.phone} onChange={handleInputChange} placeholder="Phone number" autoComplete="off" />
                  </div>
                  {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Role <span className="text-red-500">*</span></label>
                  <select className={`w-full px-3 py-2 text-sm border ${formErrors.role ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400`}
                    name="role" value={formData.role} onChange={handleInputChange} disabled={loadingRoles}>
                    {loadingRoles ? <option disabled>Loading...</option> : (
                      <>
                        <option value="">Select role</option>
                        {roles.map(role => (
                          <option key={role.role_id} value={role.role_id} selected={role.role_name.toLowerCase() === selectedUser?.role_name?.toLowerCase()}>{role.role_name}</option>
                        ))}
                      </>
                    )}
                  </select>
                  {formErrors.role && <p className="text-red-500 text-xs mt-1">{formErrors.role}</p>}
                </div>
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Password <span className="text-gray-400 font-normal">(Optional)</span></label>
                <div className="relative">
                  <input type={showPassword ? 'text' : 'password'}
                    className={`w-full px-3 py-2 text-sm border ${formErrors.password ? 'border-red-400' : 'border-gray-200'} rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 pr-10`}
                    name="userPassword" value={formData.password} onChange={handleInputChange} placeholder="Leave blank to keep current password" autoComplete="new-password" />
                  <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" onClick={() => setShowPassword(!showPassword)}>
                    <Icon icon={showPassword ? 'mdi:eye-off' : 'mdi:eye'} width={18} height={18} />
                  </button>
                </div>
                {formErrors.password && <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>}
                {formData.password && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                    {[
                      { label: '8+ chars', test: /^.{8,}$/ },
                      { label: 'Letter', test: /[a-zA-Z]/ },
                      { label: 'Number', test: /\d/ },
                      { label: 'Special char', test: /[!@#$%^&*(),.?":{}|<>]/ },
                    ].map(({ label, test }) => (
                      <span key={label} className={`flex items-center gap-1 text-xs ${test.test(formData.password) ? 'text-green-600' : 'text-gray-400'}`}>
                        <Icon icon={test.test(formData.password) ? 'mdi:check-circle' : 'mdi:circle-outline'} width={12} height={12} />
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50 rounded-b-2xl flex-shrink-0">
              <button type="button" className="px-5 py-2 rounded-xl border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-100 transition-colors" onClick={handleCloseEditModal} disabled={isEditing}>
                Cancel
              </button>
              <button type="button" className="px-5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium shadow-sm transition-colors inline-flex items-center gap-2" onClick={handleEditUser} disabled={isEditing}>
                {isEditing ? <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />Updating...</> : <><Icon icon="fluent:checkmark-24-regular" width={16} height={16} />Update User</>}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon icon="fluent:delete-24-regular" className="text-red-500 w-8 h-8" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Delete Team Member?</h3>
              <p className="text-gray-500 text-sm">
                Are you sure you want to delete <span className="font-bold text-gray-700">{selectedUser.name}</span>? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 bg-gray-50 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setSelectedUser(null); }}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-100 transition-all"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white text-sm font-semibold shadow-lg shadow-red-200 hover:bg-red-600 transition-all flex items-center justify-center gap-2"
                disabled={isDeleting}
              >
                {isDeleting ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Delete Member'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Users;
